/* hs-eslint ignored failing-rules */

/* eslint-disable react/prop-types */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Map as ImmutableMap } from 'immutable';
import PortalIdParser from 'PortalIdParser';
import I18n from 'I18n';
import { EntityTypeValues, EntityTypes, Networks, StatusMenuCodes, StatusMenuNetworkMappings, AdWordsCampaignTypes } from 'AdsUI/lib/Constants';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import UIToggle from 'UIComponents/input/UIToggle';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { AdAccountErrorStates } from 'AdsUI/records/ad-accounts/AdAccountRecord';
import CurrentUserNotConnected from './CurrentUserNotConnected';
const SETTINGS_URL = `/settings/${PortalIdParser.get()}/marketing/ads/adAccounts`;
const DISABLED_REASONS = {
  ADS_WRITE: 'ADS_WRITE',
  MISSING_NETWORK_PERMISSIONS: 'MISSING_NETWORK_PERMISSIONS',
  NETWORK_DISABLED: 'NETWORK_DISABLED',
  UNMANAGEABLE: 'UNMANAGEABLE',
  VIDEO_CAMPAIGN: 'VIDEO_CAMPAIGN',
  SMART_CAMPAIGN: 'SMART_CAMPAIGN',
  MISSING_ACCOUNT_PERMISSIONS: 'MISSING_ACCOUNT_PERMISSIONS',
  CURRENT_USER_HAS_NOT_CONNECTED: 'CURRENT_USER_HAS_NOT_CONNECTED'
};

const entityDisabledReason = (campaignEntity, entity, statusMenuCode, hasWriteScope, isMissingScopes, adAccount, entityType) => {
  const {
    network,
    type
  } = campaignEntity;
  const {
    manageable = true
  } = entity;
  const isExpired = !!campaignEntity.endTime && campaignEntity.endTime < Date.now() && entityType === EntityTypes.AD;

  if (statusMenuCode === StatusMenuCodes.DISABLED) {
    return DISABLED_REASONS.NETWORK_DISABLED;
  } else if (!hasWriteScope) {
    return DISABLED_REASONS.ADS_WRITE;
  } else if (!manageable || isExpired) {
    return DISABLED_REASONS.UNMANAGEABLE;
  } else if (network === Networks.ADWORDS && type === AdWordsCampaignTypes.VIDEO) {
    return DISABLED_REASONS.VIDEO_CAMPAIGN;
  } else if (network === Networks.ADWORDS && type === AdWordsCampaignTypes.SMART) {
    return DISABLED_REASONS.SMART_CAMPAIGN;
  } else if (isMissingScopes) {
    return DISABLED_REASONS.MISSING_NETWORK_PERMISSIONS;
  } else if (!adAccount.currentUserHasConnected) {
    return DISABLED_REASONS.CURRENT_USER_HAS_NOT_CONNECTED;
  } else if (!adAccount.canManageEntities) {
    return DISABLED_REASONS.MISSING_ACCOUNT_PERMISSIONS;
  }

  return null;
};

const UpdatePermissionsLink = props => /*#__PURE__*/_jsx(UILink, Object.assign({
  href: SETTINGS_URL,
  external: true
}, props));

const strong = props => /*#__PURE__*/_jsx("strong", Object.assign({}, props));

const renderTooltipContent = (disabledReason, network, statusCode, entityType) => {
  if (!disabledReason) {
    return null;
  }

  switch (disabledReason) {
    case DISABLED_REASONS.ADS_WRITE:
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "noWritePermission.notAuthorized",
        options: {
          action: I18n.text('noWritePermission.actions.updateCampaign')
        }
      });

    case DISABLED_REASONS.VIDEO_CAMPAIGN:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `campaigns.statusToggles.${network}.disabled.VIDEO`,
        options: {
          type: I18n.text(`campaigns.statusToggles.${network}.entityTypeMap.${entityType}`)
        }
      });

    case DISABLED_REASONS.SMART_CAMPAIGN:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `campaigns.statusToggles.${network}.disabled.SMART`,
        options: {
          type: I18n.text(`campaigns.statusToggles.${network}.entityTypeMap.${entityType}`)
        }
      });

    case DISABLED_REASONS.MISSING_NETWORK_PERMISSIONS:
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `missingPermissions.permissionsNeeded.${network}_jsx`,
        elements: {
          UpdatePermissionsLink,
          UIList,
          strong
        }
      });

    case DISABLED_REASONS.CURRENT_USER_HAS_NOT_CONNECTED:
      return /*#__PURE__*/_jsx(CurrentUserNotConnected, {
        network: network,
        action: "manageEntities"
      });

    case DISABLED_REASONS.MISSING_ACCOUNT_PERMISSIONS:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "missingPermissions.managementPermissions.message",
        options: {
          type: I18n.text(`missingPermissions.managementPermissions.types.${network}.${entityType}`),
          network: I18n.text(network.toLowerCase())
        }
      });

    default:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `campaigns.statusToggles.${network}.disabled.${statusCode}`,
        options: {
          type: I18n.text(`campaigns.statusToggles.${network}.entityTypeMap.${entityType}`)
        }
      });
  }
};

export const EntityStatusToggleButton = ({
  campaignEntity,
  entity,
  entityType,
  hasWriteScope,
  errorsByAdAccount,
  onChange,
  size,
  adAccount
}) => {
  const {
    network
  } = campaignEntity;
  const {
    status: statusCode
  } = entity;
  const statusMenuCode = StatusMenuNetworkMappings[network][statusCode];
  const disabledReason = entityDisabledReason(campaignEntity, entity, statusMenuCode, hasWriteScope, errorsByAdAccount.get(adAccount) === AdAccountErrorStates.MISSING_MANAGE_SCOPES, adAccount, entityType);
  const tooltipContent = renderTooltipContent(disabledReason, network, statusCode, entityType);
  return /*#__PURE__*/_jsx(UITooltip, {
    title: tooltipContent,
    placement: "left",
    disabled: !tooltipContent,
    use: disabledReason === DISABLED_REASONS.MISSING_NETWORK_PERMISSIONS ? 'longform' : 'default',
    children: /*#__PURE__*/_jsx(UIToggle, {
      "data-test-id": "entity-status-toggle",
      checked: statusMenuCode === StatusMenuCodes.ACTIVE,
      onChange: onChange,
      disabled: !!disabledReason,
      size: size,
      "data-test-state": disabledReason ? 'disabled' : 'enabled'
    })
  });
};
EntityStatusToggleButton.propTypes = {
  campaignEntity: PropTypes.shape({
    network: PropTypes.oneOf(Object.keys(Networks)).isRequired,
    type: PropTypes.string
  }).isRequired,
  entity: PropTypes.shape({
    manageable: PropTypes.bool,
    status: PropTypes.string.isRequired
  }).isRequired,
  entityType: PropTypes.oneOf(EntityTypeValues).isRequired,
  hasWriteScope: PropTypes.bool.isRequired,
  errorsByAdAccount: PropTypes.instanceOf(ImmutableMap).isRequired,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.string
};
EntityStatusToggleButton.defaultProps = {
  size: 'default'
};
export default EntityStatusToggleButton;